import { computed, inject, WritableComputedRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Emitter } from 'mitt';
import { useI18n } from 'vue-i18n';
import { format } from '@happys/money-format';
import { showToast, showSuccessToast, showFailToast, showLoadingToast, setToastDefaultOptions } from 'vant';

import usePwaClient from 'core/hooks/usePwaClient';
import { useClipboard } from '@vueuse/core';

import { useAppStore } from 'store/modules/appStore';
import { useRedAppStore } from 'store/modules/redAppStore';
import { useUserStore } from 'store/modules/userStore';
import { useAgentStore } from 'store/modules/agentStore';
import { useLocale, useT } from 'core/i18n';
import { toFixedTwoNum } from 'utils/precision';
import type { ToastOptions, ToastWrapperInstance } from 'vant/lib/toast/types';
import { useGameStore } from 'store/modules/useGameStore';
import { useConfigStore } from 'store/modules/configStore';
import { useDepositWithdrawStore } from 'store/modules/depositWithdraw';
import { useSocketStore } from 'store/modules/socketStore';
import { useWsLotteryStore } from 'store/modules/wsLotteryStore';
import { useLotteryStore } from 'store/modules/lotteryStore';
type NoticeType = 'primary' | 'success' | 'error' | 'warning';

interface useAppType {
    route: ReturnType<typeof useRoute>;
    router: ReturnType<typeof useRouter>;
    mitt: Emitter<any>;
    t: ReturnType<typeof useI18n>['t'];
    locale: WritableComputedRef<string>;
    appStore: ReturnType<typeof useAppStore>;
    depositWithdrawStore: ReturnType<typeof useDepositWithdrawStore>;
    redAppStore: ReturnType<typeof useRedAppStore>;
    userStore: ReturnType<typeof useUserStore>;
    agentStore: ReturnType<typeof useAgentStore>;
    gameStore: ReturnType<typeof useGameStore>;
    configStore: ReturnType<typeof useConfigStore>;
    socketStore: ReturnType<typeof useSocketStore>;
    wsLotteryStore: ReturnType<typeof useWsLotteryStore>;
    lotteryStore: ReturnType<typeof useLotteryStore>;
    formatTimeFromSeconds: Function;
    generateCombinations: Function;
    getTimeDiff: Function;
    copyText: Function;
    showToast: Function;
    showSuccessToast: Function;
    showFailToast: Function;
    showLoadingToast: (options: string | ToastOptions) => ToastWrapperInstance;
    getCurrency: Function;
    moneyFormat: Function;
    convertToKFormat: Function;
    isMobile: Function;
    toast: (options: string | ToastOptions) => ToastWrapperInstance;
    noticeMessage: (options: string | ToastOptions) => ToastWrapperInstance;
    lotteryToast: (options: string) => ToastWrapperInstance;
    moneyFormatNoDecimal: Function;
    createShareContent: (link: string, isAgent?: boolean) => string;
    openApp: (app: string, content: string) => void;
    browserDetector: BrowserDetector;
    getAndroidVersion: () => string;
    openAppFaceBook: (content: string, icon: string) => void;
    preloadImage: (url: string) => Promise<number>;
    getSrc: (val: string) => string;
}

interface BrowserDetector {
    isFacebookApp: () => boolean;
    isInstagramApp: () => boolean;
    isKwaiApp: () => boolean;
    isTikTokBrowser: () => boolean;
    isSamsungBrowser: () => boolean;
    isGoogleBrowser: () => boolean;
    isMobileDevice: () => boolean;
    isAndroidDevice: () => boolean;
    isChromeInstalled: () => Promise<boolean>;
    isMiuiBrowser: () => boolean;
    isVivoBrowser: () => boolean;
    isOppoBrowser: () => boolean;
    isUCBrowser: () => boolean;
}
// 浏览器检测工具
const browserDetector: BrowserDetector = {
    // Facebook App 内置浏览器检测
    isFacebookApp() {
        const ua = navigator.userAgent.toLowerCase();
        const fbIdentifiers = ['fb_iab', 'fb4a', 'fban', 'fbav', 'fbios', 'facebook', 'messenger', 'fbsafari'];
        return fbIdentifiers.some(id => ua.includes(id));
    },

    // Instagram App 内置浏览器检测
    isInstagramApp() {
        const ua = navigator.userAgent.toLowerCase();
        return ua.includes('instagram') || ua.includes('ig_android') || ua.includes('ig_ios');
    },

    // 快手 App 内置浏览器检测
    isKwaiApp() {
        const ua = navigator.userAgent.toLowerCase();
        return ua.includes('kwai') || ua.includes('kuaishou') || ua.includes('kwaiying');
    },

    // TikTok App 内置浏览器检测
    isTikTokBrowser() {
        const ua = navigator.userAgent.toLowerCase();
        return ua.includes('tiktok') || ua.includes('bytedance') || ua.includes('douyin') || ua.includes('musical.ly');
    },

    // 三星浏览器检测
    isSamsungBrowser() {
        const ua = navigator.userAgent.toLowerCase();
        return ua.includes('samsungbrowser') || ua.includes('samsung');
    },

    // 谷歌浏览器检测
    isGoogleBrowser() {
        const ua = navigator.userAgent.toLowerCase();

        // 排除常见的内置浏览器
        const isWebview = /(webview|wv)/i.test(ua);
        const isInApp = /nubia|huawei|vivo|oppo|xiaomi|miui|meizu/i.test(ua);

        // 检查是否是真正的 Chrome
        const isChrome = /chrome|crios/i.test(ua) && !/edge|edg|opr|opera|ucbrowser|samsung|firefox|brave|yabrowser/i.test(ua);

        // 检查 Chrome PWA 特性
        const hasPWAFeatures = 'serviceWorker' in navigator && ('BeforeInstallPromptEvent' in window || 'onbeforeinstallprompt' in window) && 'Notification' in window;

        return isChrome && !isWebview && !isInApp && hasPWAFeatures;
    },

    // 检测是否是移动设备
    isMobileDevice() {
        return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent.toLowerCase());
    },

    // 检测是否是安卓设备
    isAndroidDevice() {
        return /android/i.test(navigator.userAgent.toLowerCase());
    },

    // 检测是否安装了Chrome浏览器（仅适用于Android）
    async isChromeInstalled() {
        // 如果不是安卓设备，直接返回false
        if (!this.isAndroidDevice()) {
            return false;
        }

        try {
            // 尝试使用scheme打开Chrome
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            // 记录当前时间
            const startTime = Date.now();

            // 尝试打开Chrome的一个内部页面
            iframe.src = 'intent://chrome-native://navigate?url=chrome://version#Intent;scheme=chrome-native;package=com.android.chrome;end';

            // 等待一段时间
            await new Promise(resolve => setTimeout(resolve, 500));

            // 如果页面没有被重定向，说明Chrome可能没有安装
            const isInstalled = Date.now() - startTime > 600;

            // 清理iframe
            document.body.removeChild(iframe);

            return isInstalled;
        } catch (e) {
            console.error('检测Chrome安装状态失败:', e);
            return false;
        }
    },

    // MIUI浏览器检测
    isMiuiBrowser() {
        try {
            return navigator.userAgent.toLowerCase().indexOf("miuibrowser") > -1;
        } catch (t) {
            return false;
        }
    },

    // Vivo浏览器检测
    isVivoBrowser() {
        try {
            return navigator.userAgent.toLowerCase().indexOf("vivobrowser") > -1;
        } catch (t) {
            return false;
        }
    },

    // OPPO浏览器检测
    isOppoBrowser() {
        try {
            const t = navigator.userAgent.toLowerCase();
            return t.indexOf("oppo") > -1 || t.indexOf("heytapbrowser") > -1;
        } catch (t) {
            return false;
        }
    },

    // UC浏览器检测
    isUCBrowser() {
        try {
            return navigator.userAgent.toLowerCase().indexOf("ucbrowser") > -1;
        } catch (t) {
            return false;
        }
    },
};

export default function useApp(): useAppType {
    const t = useT();
    const locale = useLocale();
    const route = useRoute();
    const router = useRouter();
    const mitt: Emitter<any> = inject<any>('mitt');
    const systemData = computed(() => appStore.getSystemData);
    const appStore = useAppStore();
    const depositWithdrawStore = useDepositWithdrawStore();
    const redAppStore = useRedAppStore();
    const userStore = useUserStore();
    const agentStore = useAgentStore();
    const gameStore = useGameStore();
    const configStore = useConfigStore();
    const socketStore = useSocketStore();
    const wsLotteryStore = useWsLotteryStore();
    const lotteryStore = useLotteryStore();
    const { copy } = useClipboard();
    setToastDefaultOptions({ duration: 3000 });
    //判断是否是手机端
    function isMobile() {
        return navigator.userAgent.match(/(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i);
    }

    function copyText(text: string) {
        copy(text);
        showSuccessToast({
            message: t('复制成功'),
            duration: 3000,
        });
    }

    /**
     * 获取cdn图片URL
     * @param val 图片路径
     * @returns 图片URL
     */
    function getSrc(val: string) {
        if (val) {
            return `${systemData.value?.cdn || ''}${val}?v=${systemData.value?.version}`;
        }
        return '';
    }
    
    /**
     * 预加载图片
     * @param url 图片URL
     * @returns Promise<void>
     */
    function preloadImage(url: string): Promise<number> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = url;
            img.onload = () => {
                console.log('img.onload');
                resolve(1);
            };
            img.onerror = () => {
                console.log('img.onerror');
                reject(0);
            };
        });
    }

    function moneyFormat(value) {
        const currency = systemData.value?.currency;
        let money = toFixedTwoNum(value || 0);
        switch (currency) {
            case 'CNY':
            case 'INR':
                return format(money, {
                    round: false,
                });
            case 'BRL':
                if (money) {
                    return Number(money).toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                    });
                }
                return 0;
            default:
                return format(money, {
                    round: false,
                });
        }
    }

    function moneyFormatNoDecimal(value: number): string {
        const formatted = moneyFormat(value);
        const currency = systemData.value?.currency;
        // 使用逗号作为小数点的货币格式
        const commaDecimalCurrencies = ['BRL', 'EUR', 'CHF', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'ARS', 'CLP', 'COP', 'PEN', 'RUB', 'TRY', 'ZAR'];
        if (commaDecimalCurrencies.includes(currency)) {
            return formatted.toString().split(',')[0];
        }
        // 其他使用点作为小数点的货币格式
        return formatted.toString().split('.')[0];
    }

    function convertToKFormat(value: number): string {
        const formattedMoney = moneyFormat(value);
        const numericValue = parseFloat(formattedMoney.toString().replace(/,/g, '')); // 去掉千分位逗号

        const units = [
            { threshold: 1000000000, unit: 'B' },
            { threshold: 1000000, unit: 'M' },
            { threshold: 1000, unit: 'k' }
        ];

        for (const { threshold, unit } of units) {
            if (numericValue >= threshold) {
                const value = numericValue / threshold;
                const valueStr = value.toString();
                const decimalIndex = valueStr.indexOf('.');
                return decimalIndex !== -1 ? valueStr.substring(0, decimalIndex + 3) + unit : valueStr + unit;
            }
        }

        return '' + formattedMoney;
    }

    function getCurrency() {
        const currency = systemData.value?.currency?.toLocaleUpperCase();
        switch (currency) {
            case 'CNY':
                return '¥';
            case 'INR':
                return '₹';
            case 'BRL':
                return 'R$';
            // return 'Mex$'; // 方便调试
            case 'USD':
                return '$';
            case 'IDR':
                return 'Rp';
            case 'EUR':
                return '€';
            case 'MXN':
                return 'Mex$';
            case 'ARS':
                return 'ARS$';
            case 'NGN':
                return '₦';
            default:
                return '¥';
        }
    }

    function formatTimeFromSeconds(endSecond: number): string {
        const hours = Math.floor(endSecond / 3600);
        const minutes = Math.floor((endSecond % 3600) / 60);
        const seconds = endSecond % 60;

        const formattedHours = hours.toString().padStart(2, '0');
        const formattedMinutes = minutes.toString().padStart(2, '0');
        const formattedSeconds = seconds.toString().padStart(2, '0');

        return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    }

    function generateCombinations(arr: any[], k: number): any[][] {
        const n = arr.length;
        const combinations: any[][] = [];

        // 如果需要选择的元素数量大于元素总数，直接返回空数组
        if (k > n) {
            return combinations;
        }

        // 初始化组合数组和索引数组
        const combo = new Array(k);
        const indices = new Array(k);
        for (let i = 0; i < k; i++) {
            indices[i] = i;
        }

        // 生成第一个组合
        for (let i = 0; i < k; i++) {
            combo[i] = arr[indices[i]];
        }
        combinations.push(combo.slice());

        while (true) {
            let i = k - 1;
            while (i >= 0 && indices[i] === i + n - k) {
                i--;
            }

            if (i < 0) {
                break;
            }

            indices[i]++;
            for (let j = i + 1; j < k; j++) {
                indices[j] = indices[j - 1] + 1;
            }

            for (let j = 0; j < k; j++) {
                combo[j] = arr[indices[j]];
            }
            combinations.push(combo.slice());
        }

        return combinations;
    }

    function getTimeDiff(str: string) {
        if (str) {
            if (str?.includes('GMT')) {
                let offsetNum = 0;
                if (str?.includes('+')) {
                    let newArr = str?.split('+');
                    offsetNum = Number(newArr[1]);
                } else if (str?.includes('-')) {
                    let newArr = str?.split('-');
                    offsetNum = -Number(newArr[1]);
                }
                // 获取用户本地时间
                let localDate = new Date();
                // 获取用户本地时区偏移量，单位为分钟
                let timeZoneOffset = localDate.getTimezoneOffset();
                // 将偏移量转换为毫秒数
                let offsetMilliseconds = timeZoneOffset * 60 * 1000;
                // 计算目标时区的偏移量，比如 GMT+8
                let targetTimeZoneOffset = offsetNum * 60 * 60 * 1000;
                // 计算本地时间与目标时区时间的差值
                let diff = targetTimeZoneOffset + offsetMilliseconds;
                // 计算目标时区的时间
                let targetDate = new Date(localDate.getTime() + diff);
                // 输出目标时区的时间
                console.log(`${str}: ${targetDate}`);
                return targetDate;
            } else {
                // 获取用户电脑当前时间
                let now = new Date();
                // 将当前时间转换为纽约时区的时间 'America/New_York'
                let newYorkTime = now.toLocaleString('en-US', { timeZone: str });
                console.log(str + '时区时间：', newYorkTime);
                return newYorkTime;
            }
        } else {
            return new Date();
        }
    }

    function toast(options: string | ToastOptions): ToastWrapperInstance {
        const t1 = showToast(options);
        return t1;
        // return showToast(options);
    }

    function lotteryToast(options: string): ToastWrapperInstance {
        const t1 = showToast({
            message: options,
            duration: 2000,
        });
        return t1;
    }

    function createShareContent(link, shareBackgroundList) {
        // const shareBackgroundList = !isAgent ? systemData.value?.shareBackgroundList : systemData.value?.shareBackgroundListEarn;

        const userinfo = computed(() => userStore.GetUserInfo);

        let content = shareBackgroundList?.[0]?.content;
        console.log(content, 'content');

        if (content != null && content.includes('{0}')) {
            content = content.replaceAll('{0}', link ?? '');
        } else {
            content = `${content || ''}\n${link}`;
        }

        if (content.includes('{1}')) {
            content = content.replaceAll('{1}', userinfo?.value?.id.toString() ?? '');
        }

        return content;
    }

    // 添加一个检测应用是否安装的通用函数
    function checkAppInstalled(scheme: string): Promise<boolean> {
        return new Promise(resolve => {
            const timeout = 1000; // 超时时间设置为2秒

            // 创建一个隐藏的iframe
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            // 设置超时检测
            const timeoutCheck = setTimeout(() => {
                document.body.removeChild(iframe);
                resolve(false);
            }, timeout);

            // 监听iframe的加载事件
            iframe.onload = () => {
                clearTimeout(timeoutCheck);
                document.body.removeChild(iframe);
                resolve(true);
            };

            // 监听visibilitychange事件，如果页面隐藏说明应用打开了
            const visibilityCheck = () => {
                if (document.hidden) {
                    clearTimeout(timeoutCheck);
                    document.removeEventListener('visibilitychange', visibilityCheck);
                    document.body.removeChild(iframe);
                    resolve(true);
                }
            };
            document.addEventListener('visibilitychange', visibilityCheck);

            // 尝试打开应用
            iframe.src = scheme;
        });
    }

    async function openApp(app: string, content: string) {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isAndroid = /android/i.test(navigator.userAgent);
        // 检测是否在PWA环境中
        const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.matchMedia('(display-mode: fullscreen)').matches || (window.navigator as any).standalone;

        let scheme = '';
        let fallbackUrl = '';

        switch (app) {
            case 'youtube':
                scheme = isIOS ? 'youtube://' : 'vnd.youtube://';
                fallbackUrl = 'https://www.youtube.com';
                break;
            case 'facebook':
                scheme = 'fb://';
                fallbackUrl = 'https://www.facebook.com';
                break;
            case 'twitter':
                scheme = 'twitter://';
                fallbackUrl = 'https://twitter.com';
                break;
            case 'telegram':
                scheme = `tg://msg?text=${content}`;
                fallbackUrl = `https://telegram.me/share/url?url=${content}`;
                break;
            case 'whatsApp':
                scheme = `whatsapp://send?text=${content}`;
                fallbackUrl = `https://api.whatsapp.com/send?text=${content}`;
                break;
            case 'line':
                scheme = `line://msg/text/${content}`;
                fallbackUrl = `https://line.me/R/share?text=${content}`;
                break;
            case 'instagram':
                scheme = 'instagram://user?username=';
                fallbackUrl = 'https://www.instagram.com';
                break;
            case 'tikTok':
                scheme = 'snssdk1128://';
                fallbackUrl = 'https://www.tiktok.com';
                break;
            case 'kwai':
                scheme = 'kwai://';
                fallbackUrl = 'https://www.kwai.com';
                break;
            case 'messenger':
                scheme = `fb-messenger://share?link=${content}`;
                fallbackUrl = 'https://www.messenger.com';
                break;
            case 'shareChat':
                scheme = 'sharechat://';
                fallbackUrl = 'https://sharechat.com';
                break;
            case 'skype':
                scheme = 'skype://';
                fallbackUrl = 'https://www.skype.com';
                break;
        }

        try {
            // 如果是PWA环境（iOS或安卓），使用iframe或location.href方式打开
            if (isPWA) {
                console.log('PWA环境，使用优化方式打开:', scheme);

                // 创建一个隐藏的iframe
                const iframe = document.createElement('iframe');
                iframe.style.display = 'none';
                document.body.appendChild(iframe);

                // 设置超时检测
                const timeoutCheck = setTimeout(
                    () => {
                        document.body.removeChild(iframe);
                        // 如果应用未打开，则跳转到网页版
                        if (!document.hidden) {
                            console.log('应用未打开，跳转到网页版:', fallbackUrl);
                            window.location.href = fallbackUrl;
                        }
                    },
                    isIOS ? 500 : 2000
                ); // 安卓给更长的时间

                // 监听visibilitychange事件，如果页面隐藏说明应用打开了
                const visibilityCheck = () => {
                    if (document.hidden) {
                        clearTimeout(timeoutCheck);
                        document.removeEventListener('visibilitychange', visibilityCheck);
                        document.body.removeChild(iframe);
                    }
                };
                document.addEventListener('visibilitychange', visibilityCheck);

                // 尝试打开应用
                iframe.src = scheme;

                return;
            }

            // 非PWA环境使用原来的逻辑
            const isInstalled = await checkAppInstalled(scheme);
            if (isInstalled) {
                setTimeout(() => {
                    // 对于安卓设备，使用location.href而不是window.open
                    if (isAndroid) {
                        window.location.href = scheme;
                    } else {
                        window.open(scheme, '_blank');
                    }
                }, 0);
            } else {
                // 如果应用未安装，跳转到对应的网页版
                setTimeout(() => {
                    window.open(fallbackUrl, '_blank');
                }, 0);
            }
        } catch (error) {
            console.error('打开应用出错:', error);
            // 发生错误时跳转到网页版
            setTimeout(() => {
                window.open(fallbackUrl, '_blank');
            }, 0);
        }
    }

    async function openAppFaceBook(content: string, icon: string) {
        const userinfo = computed(() => userStore.GetUserInfo);

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isAndroid = /android/i.test(navigator.userAgent);
        // 检测是否在PWA环境中

        const { isPwaMode } = usePwaClient();

        // 更新Open Graph元标签

        // 构建分享链接，正确包含内容和图片
        // const shareUrl = `https://developers.facebook.com/docs/plugins/`;
        // const shareUrl = `${window.location.origin}/share.html`;

        const fbShareDomain = systemData.value?.shareInfo?.fbShareDomain;

        // domain
        // 判断systemData.value?.domain结尾是否是/ 如果不是则添加/
        const timestamp = new Date().getTime();
        const shareUrl = `${fbShareDomain}${fbShareDomain.endsWith('/') ? '' : '/'}share.html?t=${timestamp}&code=${userinfo.value?.id}`;

        console.log(shareUrl, 'shareUrl');
        console.log(window.location, '------location');
        // 使用完整的Facebook分享URL格式，包含图片参数
        // const fbShareLink = `https://www.facebook.com/sharer/sharer.php?u=https://developers.facebook.com/docs/plugins/&src=sdkpreparse`;
        const fbShareLink = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}&src=sdkpreparse`;

        console.log(fbShareLink, '----fbShareLink');

        // 针对iOS使用多种可能的scheme格式
        let scheme;
        if (isIOS) {
            scheme = 'fb://share/?link='.concat(shareUrl);
        } else {
            // Android保持原来的格式
            scheme = `fb://facewebmodal/f?href=${encodeURIComponent(fbShareLink)}`;
        }

        let fallbackUrl = fbShareLink; // 使用完整分享链接作为回退

        console.log(scheme, '=======scheme');

        try {
            // 如果是iOS PWA环境，优先使用navigator.share API
            if (isPwaMode.value && isIOS) {
                try {
                    window.location.href = scheme;
                    // await navigator.share({
                    //     title: '',
                    //     url: shareUrl,
                    //     text: content,
                    // });
                    // 分享成功，直接返回
                    return;
                } catch (shareError) {
                    console.error('navigator.share失败:', shareError);
                    // 分享失败，继续使用fb://share逻辑
                }
            }

            // 如果是PWA环境（iOS或安卓），使用iframe或location.href方式打开
            if (isPwaMode.value) {
                console.log('PWA环境，使用优化方式打开:', scheme);

                // 创建一个隐藏的iframe
                const iframe = document.createElement('iframe');
                iframe.style.display = 'none';
                document.body.appendChild(iframe);

                // 设置超时检测
                const timeoutCheck = setTimeout(
                    () => {
                        document.body.removeChild(iframe);
                        // 如果应用未打开，则不做任何操作（根据用户要求）
                    },
                    isIOS ? 500 : 2000
                ); // 安卓给更长的时间

                // 监听visibilitychange事件，如果页面隐藏说明应用打开了
                const visibilityCheck = () => {
                    if (document.hidden) {
                        clearTimeout(timeoutCheck);
                        document.removeEventListener('visibilitychange', visibilityCheck);
                        document.body.removeChild(iframe);
                    }
                };
                document.addEventListener('visibilitychange', visibilityCheck);

                // 尝试打开应用
                iframe.src = scheme;

                return;
            }

            // 非PWA环境使用原来的逻辑
            const isInstalled = await checkAppInstalled(scheme);
            if (isInstalled) {
                setTimeout(() => {
                    // 对于所有设备，使用location.href方式打开
                    window.location.href = scheme;
                }, 0);
            } else {
                // 根据用户要求，如果应用未安装，不做任何操作
            }
        } catch (error) {
            console.error('打开应用出错:', error);
            // 发生错误时不做任何回退操作（根据用户要求）
        }
    }
    const getAndroidVersion = () => {
        const userAgent = navigator.userAgent.toLowerCase();
        const match = userAgent.match(/android\s([0-9]+)/i);
        return match ? match[1] : '1';
    };

    return {
        route,
        router,
        mitt,
        t,
        locale,
        appStore,
        redAppStore,
        userStore,
        agentStore,
        gameStore,
        configStore,
        socketStore,
        lotteryStore,
        wsLotteryStore,
        depositWithdrawStore,
        formatTimeFromSeconds,
        generateCombinations,
        getTimeDiff,
        copyText,
        showToast,
        showSuccessToast,
        showFailToast,
        showLoadingToast,
        getCurrency,
        moneyFormat,
        convertToKFormat,
        toast,
        lotteryToast,
        noticeMessage: toast,
        moneyFormatNoDecimal,
        createShareContent,
        openApp,
        isMobile,
        browserDetector,
        getAndroidVersion,
        openAppFaceBook,
        preloadImage,
        getSrc,
    };
}
